/**
 * AdminDepartmentsPage — Department and team management.
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { getDepartments, createDepartment, deleteDepartment } from '../../../services/organizationService';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { ModuleLoadingState } from '../../shared/components/ModulePageHeader';

interface AdminDepartmentsPageProps {
  externalShowCreate?: boolean;
  onCreateDone?: () => void;
  externalSearch?: string;
  externalManagerFilter?: string;
}

export const AdminDepartmentsPage: React.FC<AdminDepartmentsPageProps> = ({ externalShowCreate, onCreateDone, externalSearch, externalManagerFilter }) => {
  const { workspace } = useWorkspace();
  const orgId = workspace?.organizationId ?? '';
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');

  // React to external trigger
  useEffect(() => {
    if (externalShowCreate) setShowCreate(true);
  }, [externalShowCreate]);

  const depsQuery = useQuery({
    queryKey: ['admin-departments', orgId],
    enabled: Boolean(orgId),
    queryFn: () => getDepartments(orgId),
  });

  const createMutation = useMutation({
    mutationFn: () => createDepartment(orgId, newName.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-departments', orgId] });
      setNewName('');
      setShowCreate(false);
      onCreateDone?.();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteDepartment(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-departments', orgId] }),
  });

  return (
    <RequireCapability capability="admin.access" realRoleOnly>
        <div className="enterprise-page h-full flex flex-col overflow-hidden">
          {/* Inline create form (shown when triggered from ribbon) */}
          {showCreate && (
            <div className="flex items-center gap-2 px-3 py-1.5 border-b border-[var(--border-default)] shrink-0">
              <input
                className="enterprise-input text-[12px] h-[28px] flex-1 max-w-[260px] bg-white/5 backdrop-blur border-[var(--border-default)]"
                placeholder="Nome do departamento..."
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && newName.trim() && createMutation.mutate()}
                autoFocus
              />
              <button
                type="button"
                className="enterprise-btn-primary text-[12px] h-[28px] px-3"
                disabled={!newName.trim() || createMutation.isPending}
                onClick={() => createMutation.mutate()}
              >
                {createMutation.isPending ? 'Criando...' : 'Criar'}
              </button>
              <button
                type="button"
                className="enterprise-btn-ghost text-[12px] h-[28px] px-3"
                onClick={() => { setShowCreate(false); setNewName(''); onCreateDone?.(); }}
              >
                Cancelar
              </button>
            </div>
          )}

          {depsQuery.isPending && <ModuleLoadingState label="Carregando departamentos" />}

          {!depsQuery.isPending && (
            <>
              {!depsQuery.data?.length ? (
                <p className="text-[12px] enterprise-text-secondary py-4 text-center">
                  Nenhum departamento criado ainda.
                </p>
              ) : (
                <div className="flex-1 overflow-y-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="enterprise-table-header sticky top-0 z-10">
                      <tr>
                        <th className="enterprise-table-head-cell">Departamento</th>
                        <th className="enterprise-table-head-cell">Gestor</th>
                        <th className="enterprise-table-head-cell text-right">Usuários</th>
                        <th className="enterprise-table-head-cell text-right">Projetos</th>
                        <th className="enterprise-table-head-cell">Criado em</th>
                        <th className="enterprise-table-head-cell" />
                      </tr>
                    </thead>
                    <tbody>
                      {depsQuery.data
                        .filter((dep: any) => {
                          if (externalSearch) {
                            const s = externalSearch.toLowerCase();
                            if (!dep.name?.toLowerCase().includes(s) && !dep.manager_name?.toLowerCase().includes(s)) return false;
                          }
                          if (externalManagerFilter === 'none') return !dep.manager_name;
                          if (externalManagerFilter && externalManagerFilter !== 'all') return dep.manager_name === externalManagerFilter;
                          return true;
                        })
                        .map((dep) => (
                        <tr key={dep.id} className="enterprise-table-row">
                          <td className="enterprise-table-cell font-medium">{dep.name}</td>
                          <td className="enterprise-table-cell enterprise-text-secondary">{(dep as any).manager_name || '—'}</td>
                          <td className="enterprise-table-cell text-right tabular-nums">{(dep as any).user_count ?? '—'}</td>
                          <td className="enterprise-table-cell text-right tabular-nums">{(dep as any).project_count ?? '—'}</td>
                          <td className="enterprise-table-cell enterprise-text-secondary">
                            {dep.created_at ? new Date(dep.created_at).toLocaleDateString('pt-BR') : '—'}
                          </td>
                          <td className="enterprise-table-cell text-right">
                            <button
                              type="button"
                              className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-enterprise-text-secondary hover:text-red-600 transition-colors"
                              onClick={() => deleteMutation.mutate(dep.id)}
                              title="Excluir departamento"
                            >
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
    </RequireCapability>
  );
};
